import React, { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import en from "./en";
import fr from "./fr";
import de from "./de";
import { SITE } from "../data/site";

const DICTS = { en, fr, de };

const LanguageContext = createContext({ lang: "en", t: (k) => k, langPath: (p) => p });

// Deep dot-path lookup with fallback to English, then to the raw key.
function lookup(dict, key) {
  return key.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), dict);
}

export function LanguageProvider({ lang = "en", children }) {
  const value = useMemo(() => {
    const dict = DICTS[lang] || en;
    const prefix = lang === "en" ? "" : `/${lang}`;

    const t = (key) => {
      const val = lookup(dict, key);
      if (val !== undefined) return val;
      const fallback = lookup(en, key);
      return fallback !== undefined ? fallback : key;
    };

    // Prefix an internal path with the current language segment.
    const langPath = (path) => {
      if (!path || path === "/") return prefix === "" ? "/" : prefix;
      const clean = path.startsWith("/") ? path : `/${path}`;
      return `${prefix}${clean}`;
    };

    return { lang, t, langPath, dict, prefix, languages: SITE.languages };
  }, [lang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useI18n() {
  return useContext(LanguageContext);
}

// Determine the active language from the current pathname.
export function useLangFromPath() {
  const { pathname } = useLocation();
  if (pathname === "/fr" || pathname.startsWith("/fr/")) return "fr";
  if (pathname === "/de" || pathname.startsWith("/de/")) return "de";
  return "en";
}
